// src/components/store/sections/best-seller/index.tsx
"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { ArrowRight, Flame, ChevronLeft, ChevronRight } from "lucide-react";

// 🌟 Shorashori eki folder (./) theke shob component import kora holo
import Motion3DProductCard from "./Motion3DProductCard";
import FloatingCircleProductCard from "./FloatingCircleProductCard";
import DomeProductCard from "./DomeProductCard";
import FramedProductCard from "./FramedProductCard";
import DoorHangerProductCard from "./DoorHangerProductCard";
import CurvedSliceProductCard from "./CurvedSliceProductCard";
import PastelGlassyProductCard from "./PastelGlassyProductCard";
import PastelSplitProductCard from "./PastelSplitProductCard";
import SpotlightPodiumProductCard from "./SpotlightPodiumProductCard";
import FlashUrgencyProductCard from "./FlashUrgencyProductCard";
import PolaroidTapeProductCard from "./PolaroidTapeProductCard";
import HexagonPrismProductCard from "./HexagonPrismProductCard";
import NeomorphicClayProductCard from "./NeomorphicClayProductCard";
import OrigamiRibbonProductCard from "./OrigamiRibbonProductCard";
import GlassAuroraProductCard from "./GlassAuroraProductCard";
import TicketStubProductCard from "./TicketStubProductCard";
import MagazineEditorialProductCard from "./MagazineEditorialProductCard";
import CapsulePodProductCard from "./CapsulePodProductCard";
import LayeredStackProductCard from "./LayeredStackProductCard";
import BentoSplitProductCard from "./BentoSplitProductCard";

// 🌟 Component Mapping Object
export const PRODUCT_CARD_COMPONENTS: Record<string, any> = {
  motion_3d: Motion3DProductCard,
  floating_circle: FloatingCircleProductCard,
  dome: DomeProductCard,
  framed: FramedProductCard,
  door_hanger: DoorHangerProductCard,
  curved_slice: CurvedSliceProductCard,
  pastel_glassy: PastelGlassyProductCard,
  pastel_split: PastelSplitProductCard,
  spotlight_podium: SpotlightPodiumProductCard,
  flash_urgency: FlashUrgencyProductCard,
  polaroid_tape: PolaroidTapeProductCard,
  hexagon_prism: HexagonPrismProductCard,
  neomorphic_clay: NeomorphicClayProductCard,
  origami_ribbon: OrigamiRibbonProductCard,
  glass_aurora: GlassAuroraProductCard,
  ticket_stub: TicketStubProductCard,
  magazine_editorial: MagazineEditorialProductCard,
  capsule_pod: CapsulePodProductCard,
  layered_stack: LayeredStackProductCard,
  bento_split: BentoSplitProductCard,
};

// 🌟 Helper to resolve active card variant
export function resolveProductCardComponent(variant?: string) {
  if (!variant) return Motion3DProductCard;
  return PRODUCT_CARD_COMPONENTS[variant] || Motion3DProductCard;
}

export const BEST_SELLER_VARIANTS = PRODUCT_CARD_COMPONENTS;

interface BestSellerSectionProps {
  products?: any[];
  subdomain?: string;
  variant?: string;
  design?: string;
  title?: string;
  subtitle?: string;
  itemsPerPage?: number;
}

export default function BestSellerSection({
  products = [],
  subdomain = "nextlook",
  variant,
  design,
  title = "Best Sellers",
  subtitle = "Our most popular products based on sales and customer love",
  itemsPerPage = 8,
}: BestSellerSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeVariant, setActiveVariant] = useState<string>(variant || design || "motion_3d");

  useEffect(() => {
    const syncDesign = () => {
      if (variant || design) {
        setActiveVariant(variant || design || "motion_3d");
      }
      if (typeof window !== "undefined") {
        try {
          const rawSectionDesigns = localStorage.getItem("section_designs");
          if (rawSectionDesigns) {
            const parsed = JSON.parse(rawSectionDesigns);
            if (parsed.best_sellers) {
              setActiveVariant(parsed.best_sellers);
              return;
            }
          }
          const rawThemeSettings = localStorage.getItem("theme_settings");
          if (rawThemeSettings) {
            const parsed = JSON.parse(rawThemeSettings);
            const savedDesign =
              parsed.section_designs?.best_sellers ||
              parsed.section_designs?.bestsellers ||
              parsed.best_seller_design;
            if (savedDesign) {
              setActiveVariant(savedDesign);
              return;
            }
          }
        } catch {}
      }
    };

    syncDesign();
    if (typeof window !== "undefined") {
      window.addEventListener("storage", syncDesign);
      window.addEventListener("theme_settings_updated", syncDesign);
      return () => {
        window.removeEventListener("storage", syncDesign);
        window.removeEventListener("theme_settings_updated", syncDesign);
      };
    }
  }, [variant, design]);

  useEffect(() => {
    setCurrentPage(1);
  }, [products]);

  if (!products || products.length === 0) return null;

  const CardComponent = resolveProductCardComponent(activeVariant);

  const totalProducts = products.length;
  const totalPages = Math.ceil(totalProducts / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = products.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      ref={sectionRef}
      className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 select-none space-y-6 sm:space-y-8 font-sans"
    >
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-slate-100 pb-5">
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200/60 text-xs font-black shadow-xs">
            <Flame className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
            <span>Top Rated & Trending</span>
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 tracking-tight flex items-center gap-2">
            <span>{title}</span>
            <span className="text-sm sm:text-base font-bold text-purple-600 bg-purple-50 px-2.5 py-0.5 rounded-full border border-purple-200">
              ({totalProducts})
            </span>
          </h2>

          <p className="text-xs sm:text-sm text-slate-500 font-medium max-w-xl">{subtitle}</p>
        </div>

        <Link
          href="/products?filter=best-seller"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md transition-all active:scale-95 shrink-0"
        >
          <span>View All Best Sellers</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3.5 sm:gap-6 items-stretch min-h-[400px]">
        {paginatedProducts.map((product: any, idx: number) => (
          <div key={product.id || `${currentPage}-${idx}`} className="h-full flex animate-in fade-in duration-300">
            <CardComponent product={product} index={startIndex + idx} subdomain={subdomain} />
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pt-6 sm:pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs font-bold text-slate-400">
            Showing <span className="text-slate-800">{startIndex + 1}</span> to{" "}
            <span className="text-slate-800">{Math.min(endIndex, totalProducts)}</span> of{" "}
            <span className="text-slate-800">{totalProducts}</span> Best Sellers
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 sm:px-3 sm:py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-xs font-bold flex items-center gap-1 cursor-pointer shadow-xs"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Prev</span>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
              const isActive = pageNum === currentPage;
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl text-xs font-black transition-all cursor-pointer flex items-center justify-center ${
                    isActive
                      ? "bg-slate-900 text-white shadow-md scale-105"
                      : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 sm:px-3 sm:py-2 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all text-xs font-bold flex items-center gap-1 cursor-pointer shadow-xs"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// 🌟 Component gulo re-export kore dewa holo jate onno file theke import korleo shomossha na hoy
export {
  Motion3DProductCard,
  FloatingCircleProductCard,
  DomeProductCard,
  FramedProductCard,
  DoorHangerProductCard,
  CurvedSliceProductCard,
  PastelGlassyProductCard,
  PastelSplitProductCard,
  SpotlightPodiumProductCard,
  FlashUrgencyProductCard,
  PolaroidTapeProductCard,
  HexagonPrismProductCard,
  NeomorphicClayProductCard,
  OrigamiRibbonProductCard,
  GlassAuroraProductCard,
  TicketStubProductCard,
  MagazineEditorialProductCard,
  CapsulePodProductCard,
  LayeredStackProductCard,
  BentoSplitProductCard,
};